import { useEffect, useMemo, useState } from 'react';
import axios from '../utils/Axios';
import { useAuthContext } from '../auth/useAuthContext';

// Lightweight helper to decide if onboarding should be considered complete based on current data.
const isOnboardingComplete = ({ user, hasServices }) => {
  const hasRole = !!user?.companyRole;
  const hasAvailability =
    Array.isArray(user?.config?.availability) && user.config.availability.length > 0;

  // We intentionally keep the criteria simple and data-driven:
  // role + availability + at least one service created.
  return Boolean(hasRole && hasAvailability && hasServices);
};

export default function useOnboardingStatus() {
  const { user } = useAuthContext();
  const [servicesState, setServicesState] = useState({
    loading: true,
    hasServices: false,
  });

  useEffect(() => {
    let active = true;

    const fetchServices = async () => {
      if (!user?._id) {
        setServicesState((prev) => ({ ...prev, loading: false }));
        return;
      }

      try {
        const response = await axios.get('/services');
        if (!active) return;
        const services = response?.data?.services || [];
        setServicesState({ hasServices: services.length > 0, loading: false });
      } catch (error) {
        if (!active) return;
        // Fail closed: keep onboarding locked if we can't reliably determine services
        setServicesState({ hasServices: false, loading: false });
      }
    };

    fetchServices();

    return () => {
      active = false;
    };
  }, [user?._id]);

  const status = useMemo(() => {
    const loading = servicesState.loading || !user;

    // Priority order: Backend authoritative field > localStorage (backward compat) > Manual calculation
    const backendComplete = user?.onboardingCompleted === true;
    const manualComplete = localStorage.getItem('onboardingComplete') === 'true';
    const calculatedComplete = isOnboardingComplete({ user, hasServices: servicesState.hasServices });

    const complete = loading
      ? false
      : backendComplete || manualComplete || calculatedComplete;

    return {
      loading,
      complete,
      hasSubscription: !!user?.subscription?.subscriptionId,
      hasRole: !!user?.companyRole,
      hasAvailability:
        Array.isArray(user?.config?.availability) && user.config.availability.length > 0,
      hasServices: servicesState.hasServices,
    };
  }, [servicesState, user]);

  return status;
}
